import React, { useState, useMemo } from 'react';

/**
 * MUI Components
 */
import {
    Autocomplete,
    TextField,
    CircularProgress,
    Chip,
    Box,
    Typography,
    Divider,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';

/**
 * Hooks
 */
import { useTranslation } from 'react-i18next';

const VehicleSelector = ({ vehicles, loading, value, onChange, multiple = true }) => {
    const { t } = useTranslation();
    const [selectedKind, setSelectedKind] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const normalizeString = (str) =>
        str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    const kinds = useMemo(() => {
        const map = new Map();
        vehicles.forEach((v) => {
            const kind = v.vehicle_category?.category_kind;
            const kindName = v.vehicle_category?.category_kind_name;
            if (kind && !map.has(kind)) {
                map.set(kind, kindName);
            }
        });
        return Array.from(map, ([value, label]) => ({ value, label }));
    }, [vehicles]);

    const categories = useMemo(() => {
        const map = new Map();
        vehicles.forEach((v) => {
            if (v.vehicle_category) {
                if (!selectedKind || v.vehicle_category.category_kind === selectedKind) {
                    map.set(v.vehicle_category.id, v.vehicle_category);
                }
            }
        });
        return Array.from(map.values());
    }, [vehicles, selectedKind]);

    const filteredVehicles = useMemo(() => {
        let result = vehicles;
        if (selectedKind) {
            result = result.filter((v) => v.vehicle_category?.category_kind === selectedKind);
        }
        if (selectedCategory) {
            result = result.filter((v) => v.vehicle_category?.id === selectedCategory);
        }
        return result;
    }, [vehicles, selectedKind, selectedCategory]);

    const optionLabel = (option) => {
        const parts = [option.code, option.denomination];
        if (option.plate_number) parts.push(`(${option.plate_number})`);
        return parts.filter(Boolean).join(' - ');
    };

    const filterOptions = createFilterOptions({
        stringify: (option) =>
            normalizeString(
                [option.code, option.plate_number, option.denomination]
                    .filter(Boolean)
                    .join(' ')
            ),
        trim: true,
    });

    const handleChange = (_, newValue) => {
        onChange(newValue);
    };

    const isOptionEqualToValue = (option, val) => option.id === val.id;

    const handleKindFilter = (kind) => {
        const newKind = selectedKind === kind ? null : kind;
        setSelectedKind(newKind);
        if (newKind && selectedCategory) {
            const cat = vehicles.find(
                (v) => v.vehicle_category?.id === selectedCategory
            )?.vehicle_category;
            if (cat?.category_kind !== newKind) {
                setSelectedCategory(null);
            }
        }
    };

    const handleCategoryFilter = (categoryId) => {
        setSelectedCategory((prev) => (prev === categoryId ? null : categoryId));
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                {kinds.map((kind) => (
                    <Chip
                        key={kind.value}
                        label={kind.label}
                        onClick={() => handleKindFilter(kind.value)}
                        variant={selectedKind === kind.value ? 'filled' : 'outlined'}
                        size="small"
                        sx={selectedKind === kind.value ? {
                            bgcolor: 'grey.900',
                            color: 'common.white',
                            '&:hover': { bgcolor: 'grey.800' },
                        } : undefined}
                    />
                ))}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {categories.map((cat) => (
                    <Chip
                        key={cat.id}
                        label={cat.name}
                        onClick={() => handleCategoryFilter(cat.id)}
                        color={selectedCategory === cat.id ? 'primary' : 'default'}
                        variant={selectedCategory === cat.id ? 'filled' : 'outlined'}
                        size="small"
                    />
                ))}
            </Box>

            <Autocomplete
                multiple={multiple}
                options={filteredVehicles}
                value={value}
                onChange={handleChange}
                getOptionLabel={optionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                loading={loading}
                filterOptions={filterOptions}
                groupBy={(option) => option.vehicle_category?.name ?? t('No category')}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t('Vehicles')}
                        placeholder={t('Search by code, plate or denomination')}
                        margin="normal"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                        <Chip
                            label={option.code || option.denomination}
                            {...getTagProps({ index })}
                            key={option.id}
                        />
                    ))
                }
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold">
                                {option.code} — {option.denomination}
                            </Typography>
                            {option.plate_number && (
                                <Typography variant="caption" color="text.secondary">
                                    {option.plate_number}
                                </Typography>
                            )}
                        </Box>
                    </li>
                )}
            />
        </Box>
    );
};

export default VehicleSelector;
